import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import './CommentSection.css';
import { v4 as uuidv4 } from 'uuid';
import { Icon } from './Icon.js';
import ReportModal from './ReportModal';

// Helper to format time ago in shorthand
function timeAgoShort(date) {
  const now = new Date();
  const d = new Date(date);
  const seconds = Math.floor((now - d) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w`;
  const months = Math.floor(days / 30);
  if (months >= 1 && months < 12) return `${months}mo`;
  if (months < 12) return `${weeks}w`; // 28–29 days: show 4w instead of 0mo
  const years = Math.floor(days / 365);
  return `${years}y`;
}

const CommentSection = ({ postId, postAuthorId, onCommentAdded, onCommentSubmit, onCommentCountChange }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [error, setError] = useState(null);
  const [replyTo, setReplyTo] = useState(null); // comment id being replied to
  const [replyContent, setReplyContent] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [likesInfo, setLikesInfo] = useState({}); // { [commentId]: { count, liked, likeId } }
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editContent, setEditContent] = useState('');
  // Report modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedCommentForReport, setSelectedCommentForReport] = useState(null);

  const navigate = useNavigate();

  // Fetch current user - use getSession() to avoid network requests
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id || null);
    });
  }, []);

  // Fetch comments for this post
  // silent: if true, skip setting loading state (for background refreshes)
  const fetchComments = async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    
    // Use deduplication to prevent duplicate requests (especially from React Strict Mode)
    const cacheKey = getCacheKey('fetchComments', postId);
    
    return dedupeRequest(cacheKey, async () => {
      const { data, error } = await supabase
        .from('post_comments')
        .select('*, user_profiles!post_comments_user_id_fkey(username, profile_picture_url)')
        .eq('post_id', postId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: true });
      if (error) {
        console.error('Error fetching comments:', error);
        setError('Failed to load comments');
      } else {
        setComments(data || []);
        // Fetch likes for these comments
        if (data && data.length > 0) {
          fetchLikesForComments(data.map(c => c.id));
        } else {
          setLikesInfo({});
        }
      }
      if (!silent) {
        setLoading(false);
      }
      return { data, error };
    });
  };

  // Fetch likes for a batch of comment IDs
  const fetchLikesForComments = async (commentIds) => {
    if (!commentIds.length) return setLikesInfo({});
    
    // Use deduplication to prevent duplicate requests (especially from React Strict Mode)
    // Sort comment IDs for consistent cache key
    const sortedIds = [...commentIds].sort();
    const cacheKey = getCacheKey('fetchLikesForComments', sortedIds);
    
    return dedupeRequest(cacheKey, async () => {
      // Use getSession() to avoid network requests
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      // Fetch all likes for these comments
      const { data: likeRows } = await supabase
        .from('post_comments_likes')
        .select('comment_id, user_id, id')
        .in('comment_id', commentIds);
      // Count likes per comment
      let likeCounts = {};
      let likedMap = {};
      (likeRows || []).forEach(row => {
        likeCounts[row.comment_id] = (likeCounts[row.comment_id] || 0) + 1;
        if (userId && row.user_id === userId) {
          likedMap[row.comment_id] = row.id;
        }
      });
      // Set info
      let info = {};
      commentIds.forEach(cid => {
        info[cid] = {
          count: likeCounts[cid] || 0,
          liked: !!likedMap[cid],
          likeId: likedMap[cid] || null,
        };
      });
      setLikesInfo(info);
      return info;
    });
  };

  // Like/unlike handler for a comment
  const handleLikeToggle = async (commentId) => {
    console.log('Like button clicked for comment:', commentId, 'userId:', userId);
    const info = likesInfo[commentId] || { count: 0, liked: false, likeId: null };
    if (!userId) {
      console.warn('User not signed in');
      return;
    }
    if (!info.liked) {
      // Like
      const { data, error } = await supabase
        .from('post_comments_likes')
        .insert({ user_id: userId, comment_id: commentId })
        .select('id')
        .single();
      if (error) {
        console.error('Like error:', error);
      } else {
        console.log('Like inserted:', data);
        
        // Note: Notification is automatically created by database trigger
        
        // Update local state optimistically instead of refetching all comment likes
        setLikesInfo((prev) => ({
          ...prev,
          [commentId]: {
            count: (prev[commentId]?.count || 0) + 1,
            liked: true,
            likeId: data.id,
          },
        }));
      }
    } else {
      // Unlike
      const { error } = await supabase
        .from('post_comments_likes')
        .delete()
        .eq('id', info.likeId);
      if (error) {
        console.error('Unlike error:', error);
      } else {
        console.log('Like removed');
        // Update local state optimistically instead of refetching all comment likes
        setLikesInfo((prev) => ({
          ...prev,
          [commentId]: {
            count: Math.max(0, (prev[commentId]?.count || 0) - 1),
            liked: false,
            likeId: null,
          },
        }));
      }
    }
  };

  // Edit comment handler
  const handleEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditContent(comment.content);
  };

  // Save edited comment
  const handleSaveEdit = async (commentId) => {
    if (!editContent.trim()) return;
    const { error } = await supabase
      .from('post_comments')
      .update({ content: editContent.trim(), updated_at: new Date().toISOString() })
      .eq('id', commentId);
    if (!error) {
      setEditingCommentId(null);
      setEditContent('');
      // Use silent refresh to avoid UI flash when editing comment
      fetchComments(true);
    } else {
      setError('Failed to update comment');
    }
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditContent('');
  };

  // Delete comment handler (soft delete)
  const handleDeleteComment = async (commentId) => {
    const { error } = await supabase
      .from('post_comments')
      .update({ is_deleted: true, content: '[deleted]' })
      .eq('id', commentId);
    if (!error) {
      // Notify parent to decrement comment count optimistically
      if (onCommentCountChange) {
        onCommentCountChange(-1);
      }
      // Use silent refresh to avoid UI flash when deleting comment
      fetchComments(true);
    } else {
      setError('Failed to delete comment');
    }
  };

  // Use ref to store the latest fetchComments function
  const fetchCommentsRef = useRef();
  fetchCommentsRef.current = fetchComments;

  useEffect(() => {
    fetchComments();
    // Optionally, subscribe to real-time updates here
    // eslint-disable-next-line
  }, [postId]);

  // Expose comment refresh function to parent - always calls the latest fetchComments
  // silent: if true, skip loading state to avoid UI flash
  const refreshComments = useCallback((silent = true) => {
    if (fetchCommentsRef.current) {
      try {
        fetchCommentsRef.current(silent);
      } catch (error) {
        console.error('Error refreshing comments:', error);
      }
    } else {
      console.warn('fetchCommentsRef.current is not set');
    }
  }, []);

  // Expose refresh function to parent via callback - set it up immediately and on changes
  useEffect(() => {
    if (onCommentSubmit) {
      onCommentSubmit(refreshComments);
    }
  }, [onCommentSubmit, refreshComments]);

  // Add a reply to a comment
  const handleAddReply = async (parentId) => {
    if (!userId || !replyContent.trim()) return;
    setReplySubmitting(true);
    const { error } = await supabase
      .from('post_comments')
      .insert({ post_id: postId, user_id: userId, content: replyContent.trim(), parent_comment_id: parentId });
    setReplySubmitting(false);
    if (!error) {
      // Notify parent to increment comment count optimistically
      if (onCommentCountChange) {
        onCommentCountChange(1);
      }
      setReplyContent('');
      setReplyTo(null);
      // Use silent refresh to avoid UI flash when adding reply
      fetchComments(true);
      // Don't call onCommentAdded here to avoid duplicate fetching
      // Real-time subscription will handle count updates if needed
    } else {
      setError('Failed to add reply');
    }
  };

  // Build a nested comment tree
  const buildTree = (comments) => {
    const map = {};
    comments.forEach(c => { map[c.id] = { ...c, replies: [] }; });
    const roots = [];
    comments.forEach(c => {
      if (c.parent_comment_id && map[c.parent_comment_id]) {
        map[c.parent_comment_id].replies.push(map[c.id]);
      } else {
        roots.push(map[c.id]);
      }
    });
    return roots;
  };

  // Render a comment and its replies recursively
  const renderComment = (c, depth = 0) => {
    const isOwnComment = c.user_id === userId;
    const commentUsername = c.user_profiles?.username || 'User';
    
    return (
      <div
        key={c.id}
        className={`${depth > 0 ? 'nested-comment' : 'comment-item'} ${isOwnComment ? 'comment-item--owned' : ''}`}
      >
        <div className="comment-content-wrapper">
          <div className="comment-avatar">
            {c.user_profiles?.profile_picture_url ? (
              <img 
                src={c.user_profiles.profile_picture_url} 
                alt={`${commentUsername}'s profile`}
                className="comment-avatar-image"
              />
            ) : (
              <span className="comment-avatar-fallback">
                {commentUsername.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="comment-body">
            <div className="comment-header">
              <span
                role="button"
                tabIndex={0}
                className="comment-username"
                onClick={() => navigate(`/user/${c.user_id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/user/${c.user_id}`); } }}
                title={`View ${commentUsername}'s profile`}
              >
                {commentUsername}
              </span>
              {/* Edit/Delete icons for comment owner - positioned before timestamp */}
              {isOwnComment && !c.is_deleted && (
                <div className="comment-actions">
                  <button className="icon-btn icon-btn--xs icon-btn--primary" title="Edit" onClick={() => handleEditComment(c)}>
                    <Icon name="Edit" className="icon--xs" />
                  </button>
                  <button className="icon-btn icon-btn--xs icon-btn--danger" title="Delete" onClick={() => handleDeleteComment(c.id)}>
                    <Icon name="Trash2" className="icon--xs" />
                  </button>
                </div>
              )}
              
              {/* Report button for non-own comments */}
              {!isOwnComment && userId && !c.is_deleted && (
                <div className="comment-actions">
                  <button 
                    className="icon-btn icon-btn--xs icon-btn--secondary" 
                    title="Report Comment" 
                    onClick={() => {
                      setSelectedCommentForReport(c);
                      setShowReportModal(true);
                    }}
                  >
                    <Icon name="AlertTriangle" className="icon--xs" />
                  </button>
                </div>
              )}
              <span className="comment-time">
                {c.updated_at && c.updated_at !== c.created_at ? (
                  <>
                    {timeAgoShort(c.updated_at)} <span style={{ fontStyle: 'italic', color: 'var(--color-text-muted)' }}>edited</span>
                  </>
                ) : (
                  timeAgoShort(c.created_at)
                )}
              </span>
            </div>
          
            {editingCommentId === c.id ? (
              <form className="comment-edit-form" onSubmit={e => { e.preventDefault(); handleSaveEdit(c.id); }}>
                <input
                  type="text"
                  value={editContent}
                  onChange={e => setEditContent(e.target.value)}
                  autoFocus
                />
                <div className="comment-edit-actions">
                  <button type="submit" className="btn btn--primary btn--sm">Save</button>
                  <button type="button" className="btn btn--tertiary btn--sm" onClick={handleCancelEdit}>Cancel</button>
                </div>
              </form>
            ) : (
              <div 
                className="comment-content"
                data-deleted={c.is_deleted}
              >
                {c.content}
              </div>
            )}
            
            {/* Comment actions */}
            <div className="comment-footer">
              <button
                className={`comment-stat ${likesInfo[c.id]?.liked ? 'comment-stat--liked' : ''}`}
                title={likesInfo[c.id]?.liked ? 'Unlike comment' : 'Like comment'}
                onClick={() => handleLikeToggle(c.id)}
                disabled={!userId}
              >
                <Icon 
                  name="ThumbsUp" 
                  className={`icon--sm ${likesInfo[c.id]?.liked ? 'icon--primary' : 'icon--muted'}`}
                />
                <span>{likesInfo[c.id]?.count || 0}</span>
              </button>
              
              <button 
                className="comment-stat" 
                onClick={() => setReplyTo(c.id)} 
                disabled={!userId} 
                title={userId ? 'Reply' : 'Sign in to reply'}
              >
                <Icon name="Reply" className="icon--sm icon--muted" />
                <span>Reply</span>
              </button>
            </div>
            
            {replyTo === c.id && (
              <form
                className="comment-reply-form"
                onSubmit={e => { e.preventDefault(); handleAddReply(c.id); }}
              >
                <input
                  type="text"
                  value={replyContent}
                  onChange={e => setReplyContent(e.target.value)}
                  placeholder={userId ? 'Write a reply...' : 'Sign in to reply'}
                  disabled={!userId || replySubmitting}
                />
                <button 
                  type="submit" 
                  disabled={!userId || !replyContent.trim() || replySubmitting} 
                  className="btn btn--primary btn--sm"
                >
                  {replySubmitting ? '...' : 'Reply'}
                </button>
                <button 
                  type="button" 
                  onClick={() => { setReplyTo(null); setReplyContent(''); }} 
                  className="btn btn--tertiary btn--sm"
                >
                  Cancel
                </button>
              </form>
            )}
          </div>
        </div>

        {c.replies && c.replies.length > 0 && (
          <div className="comment-replies">
            {c.replies.map(r => renderComment(r, depth + 1))}
          </div>
        )}
      </div>
  );
};

  // Render comments (nested)
  const commentTree = buildTree(comments);

  return (
    <div className="comment-section">
      <div className="comment-section-header">
        Comments
      </div>
      
      {loading ? (
        <div className="comment-loading">Loading comments...</div>
      ) : error ? (
        <div className="comment-error">{error}</div>
      ) : (
        <div className="comments-list">
          {commentTree.length === 0 ? (
            <div className="comment-empty">
              No comments yet. Be the first to share your thoughts!
            </div>
          ) : (
            commentTree.map(c => renderComment(c))
          )}
        </div>
      )}
      
      {/* Report Modal */}
      {showReportModal && selectedCommentForReport && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => {
            setShowReportModal(false);
            setSelectedCommentForReport(null);
          }}
          post={null}
          comment={selectedCommentForReport}
          message={null}
          reportedUserId={selectedCommentForReport.user_id}
          reportedUsername={selectedCommentForReport.user_profiles?.username || 'User'}
        />
      )}
    </div>
  );
};

export default CommentSection; 